import type { Patch } from "@freightsimple/shared/types";
import AddressInput from "../Inputs/LocationsAddressInput";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { locationsErrorMessagesForLocation } from "../Validators/locationsErrorMessagesForLocation";
import { useOnce } from "@/Hooks/useOnce";
import { usePostalCodesApi } from "@/Hooks/usePostalCodesApi";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import { useState } from "react";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";

interface LocationsAddLocationDialogAddressPage {
  location: AddLocationLocationType;
  setLocation: (_: AddLocationLocationType) => void;
  onSwitchToContactPage: () => void;
  onPrevious: () => void;
}

function LocationsAddLocationDialogAddressPage(
  props: LocationsAddLocationDialogAddressPage,
) {
  const { location, setLocation, onSwitchToContactPage, onPrevious } = props;
  const [forceValidation, setForceValidation] = useState(false);
  const [addressOptions, setAddressOptions] = useState<Array<string>>([]);

  const postalCode = location.address.postalCode;
  const postalCodesApi = usePostalCodesApi();

  if (!postalCode) {
    throw new Error("Missing postalCode");
  }

  function updateAddress(addressUpdates: Patch<Address>) {
    const address = {
      ...location.address,
      ...addressUpdates,
    };

    setLocation({
      ...location,
      address,
    });
  }

  useOnce(async () => {
    const _addressOptions = await postalCodesApi.lookUpPotentialAddresses({
      postalCode,
    });
    setAddressOptions(_addressOptions);

    if (
      _addressOptions.length === 1 &&
      (props.location.address.addressLine === "" ||
        props.location.address.addressLine === undefined)
    ) {
      updateAddress({ addressLine: _addressOptions[0] });
    }
  });

  // TODO: Make this depend on if you're on the address book screen / book shipment
  const locationErrorMessages = locationsErrorMessagesForLocation(
    location,
    false,
  );

  function setBusinessName(businessName: string) {
    setLocation({
      ...location,
      businessName,
    });
  }

  function isValid() {
    if (locationErrorMessages.address.addressLine !== undefined) {
      return false;
    }

    if (locationErrorMessages.businessName !== undefined) {
      return false;
    }

    return true;
  }

  async function onNext() {
    if (isValid()) {
      onSwitchToContactPage();
    } else {
      setForceValidation(true);
    }
  }

  return (
    <Stack gap={8}>
      {/* No width wrapper: the Stack lets the address grid fill the
       * dialog body, which is the width it was pinned to anyway. */}
      <AddressInput
        enabled={true}
        businessName={props.location.businessName || ""}
        businessNameChanged={setBusinessName}
        locationType={props.location.locationType!}
        distributionWarehouseBrand={props.location.distributionWarehouseBrand}
        address={props.location.address}
        addressChanged={updateAddress}
        forceValidation={forceValidation}
        businessNameErrorMessages={locationErrorMessages.businessName}
        addressErrorMessages={locationErrorMessages.address}
        addressOptions={addressOptions}
        allowCityAndPostalCodeEditing={false}
        warnAboutPotentialBusinessNameErrors={true}
      />
      <Row justify="between">
        <Button onClick={onPrevious}>Previous</Button>
        <Button primary onClick={onNext}>
          Next
        </Button>
      </Row>
    </Stack>
  );
}

export default LocationsAddLocationDialogAddressPage;
